/**
 * Recommendations - Dual Path: Bag Health Check + Build My Bag
 * 
 * Two paths for different user needs:
 * 1. BAG HEALTH CHECK (default) - Problem-first diagnosis for existing golfers
 * 2. BUILD MY BAG - Full bag builder for new golfers or complete rebuilds
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { 
  Activity, 
  CheckCircle, 
  TrendingUp, 
  AlertTriangle,
  Target,
  Award,
  Lightbulb,
  RefreshCw,
  Wrench,
  Plus,
  ShoppingBag,
  Sparkles,
  Clock
} from "lucide-react";
import { Link } from "react-router-dom";
import SEO, { PAGE_SEO } from "./SEO";
import { UserProfile } from "../types";
import { analyzeBag, BagProblem, BagHealthReport } from "../utils/bagAnalyzer";
import ProblemCard, { ClubSolution } from "./ProblemCard";
import { aiService } from "../services/aiService";

interface RecommendationsProps {
  userProfile: UserProfile | null;
}

type ViewMode = 'health-check' | 'build-bag';

const Recommendations: React.FC<RecommendationsProps> = ({ userProfile }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('health-check');
  const [healthReport, setHealthReport] = useState<BagHealthReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [solutionsMap, setSolutionsMap] = useState<Map<string, ClubSolution[]>>(new Map());
  const [loadingSolutions, setLoadingSolutions] = useState<Set<string>>(new Set());
  const [analysisToast, setAnalysisToast] = useState<{ show: boolean; message: string; type: 'success' | 'info' }>({ show: false, message: '', type: 'info' });

  // Determine if user is a new golfer (empty bag or minimal clubs)
  const isNewGolfer = !userProfile?.golfBag || userProfile.golfBag.length < 3;

  // Create a comprehensive hash of golfBag to detect ALL relevant changes
  // This ensures Health Check re-analyzes when distances, clubs, or manual flags change
  const distanceHash = useMemo(() => {
    if (!userProfile?.golfBag) return '';
    // Include: club count, each club's type, distance, and manual flag
    const bagHash = userProfile.golfBag
      .map(c => `${c.type}:${c.distance || 0}:${c.isManuallySet ? 'M' : 'E'}`)
      .sort()
      .join(',');
    // Prefix with bag length to detect additions/removals
    return `${userProfile.golfBag.length}|${bagHash}`;
  }, [userProfile?.golfBag]);

  // Show toast notification
  const showToast = useCallback((message: string, type: 'success' | 'info' = 'success') => {
    setAnalysisToast({ show: true, message, type });
    setTimeout(() => setAnalysisToast({ show: false, message: '', type: 'info' }), 4000);
  }, []);

  // AI-powered bag analysis
  const analyzeUserBag = useCallback(async () => {
    if (!userProfile) return;
    
    setLoading(true);
    
    // Clear cached solutions when re-analyzing
    setSolutionsMap(new Map());
    
    // Track start time for minimum loading duration (3 seconds for perceived value)
    const startTime = Date.now();
    const MIN_LOADING_TIME = 3000; // 3 seconds minimum
    
    // Step 1: Run local analysis (fast)
    const localReport = analyzeBag(userProfile);
    const analyzedAt = new Date();
    
    // Step 2: Enhance with AI analysis (async)
    let result: { report: BagHealthReport; success: boolean };
    
    try {
      const aiResult = await aiService.getAIBagAnalysis(userProfile, localReport.problems);
      
      // Merge AI insights with local report
      // Map enhanced problems back to original problems to preserve proper types
      const enhancedProblems = aiResult.enhancedProblems.map(ep => {
        const original = localReport.problems.find(p => p.id === ep.id);
        if (original) {
          // Spread original to preserve all typed fields, then add AI fields
          return {
            ...original,
            aiPriority: ep.aiPriority,
            aiReason: ep.aiReason
          };
        }
        return original!;
      }).filter(Boolean) as BagProblem[];

      result = {
        report: {
          ...localReport,
          problems: enhancedProblems,
          aiInsights: aiResult.aiInsights,
          priorityRecommendation: aiResult.priorityRecommendation,
          scoringImpact: aiResult.scoringImpact,
          analyzedAt
        },
        success: true
      };
    } catch (error) {
      console.error('AI analysis failed, using local results:', error);
      result = {
        report: { ...localReport, analyzedAt },
        success: false
      };
    }
    
    // Ensure minimum loading time for perceived value
    const elapsed = Date.now() - startTime;
    if (elapsed < MIN_LOADING_TIME) {
      await new Promise(resolve => setTimeout(resolve, MIN_LOADING_TIME - elapsed));
    }
    
    // Now update state
    setHealthReport(result.report);
    showToast(result.success ? '✨ AI analysis complete!' : 'Analysis complete (AI unavailable)', result.success ? 'success' : 'info');
    setLoading(false);
  }, [userProfile, showToast]);

  // Track previous hash to detect data changes vs. initial load
  const prevDistanceHashRef = useRef<string>('');

  // Re-analyze whenever distances change (not just when userProfile reference changes)
  useEffect(() => {
    if (userProfile && !isNewGolfer) {
      const isDataChange = prevDistanceHashRef.current !== '' && prevDistanceHashRef.current !== distanceHash;
      if (isDataChange) {
        showToast('Bag data changed — re-analyzing...', 'info');
      }
      prevDistanceHashRef.current = distanceHash;
      analyzeUserBag();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distanceHash, isNewGolfer]);

  const handleLoadSolutions = async (problem: BagProblem) => {
    if (!userProfile || solutionsMap.has(problem.id)) return;
    
    setLoadingSolutions(prev => new Set(prev).add(problem.id));
    
    try {
      // Build current club distances from golfBag (single source of truth)
      const clubDistances = (userProfile.golfBag || [])
        .filter(club => club.distance && club.distance > 0)
        .map(club => ({ club: club.type, distance: club.distance! }));
      
      // Get AI-powered solutions for this specific problem with current distances
      const solutions = await aiService.getAISolutionsForProblem(
        problem,
        userProfile,
        userProfile.equipmentTier || 'medium',
        clubDistances
      );
      
      setSolutionsMap(prev => new Map(prev).set(problem.id, solutions));
    } catch (error) {
      console.error('Failed to load solutions:', error);
      // Solutions will fall back to placeholders in ProblemCard
    }
    
    setLoadingSolutions(prev => {
      const next = new Set(prev);
      next.delete(problem.id);
      return next;
    });
  };

  // No profile state
  if (!userProfile) {
    return <NoProfileState />;
  }

  return (
    <>
      <SEO {...PAGE_SEO.recommendations} url="/recommendations" />
    <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6">
      {/* Toast Notification */}
      {analysisToast.show && (
        <div className={`fixed top-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-50 px-4 py-3 rounded-lg shadow-lg animate-pulse ${
          analysisToast.type === 'success' 
            ? 'bg-green-500 text-white' 
            : 'bg-blue-500 text-white'
        }`}>
          <div className="flex items-center gap-2">
            {analysisToast.type === 'success' ? (
              <Sparkles className="w-5 h-5" />
            ) : (
              <Activity className="w-5 h-5" />
            )}
            <span className="font-medium">{analysisToast.message}</span>
          </div>
        </div>
      )}

      {/* Header - Removed since ClubOptimizerHub has page header */}

      {/* Path Toggle */}
      <div className="bg-white rounded-xl border border-gray-200 p-1 flex">
        <button
          onClick={() => setViewMode('health-check')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-semibold transition-all ${
            viewMode === 'health-check'
              ? 'bg-blue-600 text-white shadow-md'
              : 'text-gray-600 hover:bg-gray-50'
          }`}
        >
          <Activity className="w-5 h-5" />
          <span>Health Check</span>
        </button>
        <button
          onClick={() => setViewMode('build-bag')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-semibold transition-all ${
            viewMode === 'build-bag'
              ? 'bg-purple-600 text-white shadow-md'
              : 'text-gray-600 hover:bg-gray-50'
          }`}
        >
          <ShoppingBag className="w-5 h-5" />
          <span>Build Bag</span>
        </button>
      </div>

      {/* Path Description */}
      <div className={`rounded-xl p-4 ${viewMode === 'health-check' ? 'bg-blue-50 border border-blue-200' : 'bg-purple-50 border border-purple-200'}`}>
        <div className="flex items-start gap-3">
          {viewMode === 'health-check' ? (
            <>
              <Activity className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-900">Diagnose & Optimize Your Current Bag</p>
                <p className="text-sm text-blue-700">Identify gapping issues, weak spots, and get targeted upgrade recommendations.</p>
              </div>
            </>
          ) : (
            <>
              <ShoppingBag className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-purple-900">Start Fresh or Complete Rebuild</p>
                <p className="text-sm text-purple-700">Perfect for new golfers or those wanting a complete bag overhaul with matched sets.</p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Content Based on Mode */}
      {viewMode === 'health-check' ? (
        <BagHealthCheckView 
          userProfile={userProfile}
          healthReport={healthReport}
          loading={loading}
          isNewGolfer={isNewGolfer}
          solutionsMap={solutionsMap}
          loadingSolutions={loadingSolutions}
          onAnalyze={analyzeUserBag}
          onLoadSolutions={handleLoadSolutions}
        />
      ) : (
        <BuildMyBagView userProfile={userProfile} />
      )}
    </div>
    </>
  );
};

/**
 * Bag Health Check View - Problem-first diagnosis
 */
interface BagHealthCheckViewProps {
  userProfile: UserProfile;
  healthReport: BagHealthReport | null;
  loading: boolean;
  isNewGolfer: boolean;
  solutionsMap: Map<string, ClubSolution[]>;
  loadingSolutions: Set<string>;
  onAnalyze: () => void;
  onLoadSolutions: (problem: BagProblem) => void;
}

const BagHealthCheckView: React.FC<BagHealthCheckViewProps> = ({
  userProfile,
  healthReport,
  loading,
  isNewGolfer,
  solutionsMap,
  loadingSolutions,
  onAnalyze,
  onLoadSolutions
}) => {
  // Ref for auto-scrolling to issues section
  const issuesRef = useRef<HTMLDivElement>(null);
  
  // Auto-scroll to issues section after analysis completes
  useEffect(() => {
    if (!loading && healthReport && healthReport.problems.length > 0 && issuesRef.current) {
      // Small delay to allow the UI to render
      setTimeout(() => {
        issuesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 500);
    }
  }, [loading, healthReport]);
  
  // New golfer - suggest Build My Bag instead
  if (isNewGolfer) {
    return (
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 text-center">
        <Activity className="w-16 h-16 text-blue-500 mx-auto mb-4" />
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">Not Enough Club Data</h2>
        <p className="text-gray-600 mb-6">
          We need at least 3 clubs in your bag to run a meaningful health check. 
          Try the <strong>Build My Bag</strong> option to get started with a complete set!
        </p>
        <Link 
          to="/my-golf-bag" 
          className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
        >
          <Plus size={20} />
          Add Clubs to My Bag
        </Link>
      </div>
    );
  }

  // Loading state - AI Analysis in progress
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="relative">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600 mb-4"></div>
          <Sparkles className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-6 h-6 text-blue-600 animate-pulse" />
        </div>
        <p className="text-gray-900 font-semibold mb-1">AI Analyzing Your Bag...</p>
        <p className="text-gray-500 text-sm">Getting personalized insights from ClubIQ AI</p>
      </div>
    );
  }

  // No report yet
  if (!healthReport) {
    return (
      <div className="text-center py-16">
        <Activity className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-gray-900 mb-2">Ready to Analyze</h2>
        <p className="text-gray-600 mb-6">Click below to analyze your golf bag for optimization opportunities.</p>
        <button
          onClick={onAnalyze}
          className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
        >
          <Activity size={20} />
          Analyze My Bag
        </button>
      </div>
    );
  }

  const { overallScore, problems, strengths, summary, aiInsights, priorityRecommendation, scoringImpact, analyzedAt } = healthReport;
  const criticalProblems = problems.filter(p => p.severity === 'critical');
  const highProblems = problems.filter(p => p.severity === 'high');
  const mediumProblems = problems.filter(p => p.severity === 'medium');
  const lowProblems = problems.filter(p => p.severity === 'low');

  // Format timestamp
  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };


  return (
    <div className="space-y-4">
      {/* Hero: Priority Recommendation (if available) */}
      {priorityRecommendation && (
        <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-2xl p-5 text-white">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <Target className="w-5 h-5" />
            </div>
            <div>
              <p className="text-white/80 text-xs font-medium uppercase tracking-wide">Top Priority Fix</p>
              <p className="text-base font-semibold mt-1">{priorityRecommendation}</p>
              {scoringImpact && (
                <p className="text-indigo-200 text-sm flex items-center gap-1 mt-2">
                  <TrendingUp className="w-4 h-4" />
                  {scoringImpact}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Compact Score Bar - demoted from hero position */}
      <div className="flex items-center justify-between bg-white border border-gray-200 rounded-xl p-3">
        <div className="flex items-center gap-3">
          <div className="relative w-10 h-10 flex-shrink-0">
            <svg className="w-10 h-10 transform -rotate-90">
              <circle cx="20" cy="20" r="16" stroke="#e5e7eb" strokeWidth="4" fill="none" />
              <circle
                cx="20" cy="20" r="16"
                stroke={overallScore >= 80 ? '#22c55e' : overallScore >= 60 ? '#f59e0b' : '#ef4444'}
                strokeWidth="4" fill="none" strokeLinecap="round"
                strokeDasharray={`${(overallScore / 100) * 100.5} 100.5`}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-xs font-bold text-gray-900">{overallScore}</span>
            </div>
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">Bag Health: {overallScore}/100</p>
            <p className="text-xs text-gray-500 line-clamp-1">{summary}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {analyzedAt && (
            <span className="text-xs text-gray-400 flex items-center gap-1 whitespace-nowrap">
              <Clock className="w-3 h-3" />
              {formatTimeAgo(analyzedAt)}
            </span>
          )}
          <button
            onClick={onAnalyze}
            className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1 transition-colors whitespace-nowrap font-medium"
          >
            <RefreshCw className="w-3 h-3" />
            Re-analyze
          </button>
        </div>
      </div>

      {/* AI Insights - concise list */}
      {aiInsights && aiInsights.length > 0 && (
        <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-purple-600" />
            <h3 className="text-sm font-bold text-purple-900">AI Insights</h3>
          </div>
          <ul className="space-y-1.5">
            {aiInsights.map((insight, index) => (
              <li key={index} className="flex items-start gap-2 text-purple-800 text-sm">
                <Lightbulb className="w-3.5 h-3.5 text-purple-500 flex-shrink-0 mt-0.5" />
                {insight}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Problem Summary Stats */}
      {problems.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard 
            count={criticalProblems.length} 
            label="Critical" 
            color="red" 
          />
          <StatCard 
            count={highProblems.length} 
            label="High Priority" 
            color="orange" 
          />
          <StatCard 
            count={mediumProblems.length} 
            label="Medium" 
            color="yellow" 
          />
          <StatCard 
            count={lowProblems.length} 
            label="Optimizations" 
            color="green" 
          />
        </div>
      )}

      {/* Problems List */}
      {problems.length > 0 ? (
        <div ref={issuesRef} className="space-y-4 scroll-mt-4">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Target className="w-5 h-5 text-blue-600" />
            Issues Found ({problems.length})
          </h3>
          
          <p className="text-sm text-gray-600">
            Tap any issue to see solutions matched to your {userProfile.equipmentTier === 'budget' ? 'Value' : userProfile.equipmentTier === 'premium' ? 'Premium' : 'Mid-Range'} budget.
          </p>
          
          <div className="space-y-3">
            {problems.map((problem) => (
              <ProblemCard
                key={problem.id}
                problem={problem}
                userProfile={userProfile}
                solutions={solutionsMap.get(problem.id)}
                onLoadSolutions={onLoadSolutions}
                isLoadingSolutions={loadingSolutions.has(problem.id)}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-green-50 rounded-2xl p-8 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-green-900 mb-2">Your Bag Looks Great!</h3>
          <p className="text-green-700">
            No significant issues detected. Keep playing and updating your distances for ongoing optimization.
          </p>
        </div>
      )}

      {/* Strengths */}
      {strengths.length > 0 && (
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6">
          <h3 className="text-lg font-bold text-green-900 mb-3 flex items-center gap-2">
            <Award className="w-5 h-5 text-green-600" />
            Bag Strengths
          </h3>
          <ul className="space-y-2">
            {strengths.map((strength, index) => (
              <li key={index} className="flex items-center gap-2 text-green-800">
                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                {strength}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Tip */}
      <div className="bg-blue-50 rounded-xl p-4 flex items-start gap-3">
        <Lightbulb className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-blue-900 font-medium">Pro Tip</p>
          <p className="text-sm text-blue-700">
            Update your club distances regularly to get the most accurate gapping analysis. 
            Even a range session's worth of data helps!
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * Build My Bag View - For new golfers or complete rebuilds
 */
interface BuildMyBagViewProps {
  userProfile: UserProfile;
}

const BuildMyBagView: React.FC<BuildMyBagViewProps> = ({ userProfile }) => {
  const budgetTier = userProfile.equipmentTier || 'medium';
  const budgetLabel = budgetTier === 'budget' ? 'Value' : budgetTier === 'premium' ? 'Premium' : 'Mid-Range';

  return (
    <div className="space-y-6">
      {/* Quick Overview */}
      <div className="bg-gradient-to-br from-purple-600 to-indigo-700 rounded-2xl p-6 text-white">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-white/20 rounded-xl">
            <Wrench className="w-8 h-8" />
          </div>
          <div>
            <h2 className="text-xl font-bold mb-2">Build Your Perfect Bag</h2>
            <p className="text-purple-100">
              Get AI-matched complete sets tailored to your skill level, budget, and playing style.
            </p>
          </div>
        </div>
      </div>

      {/* Current Profile Summary */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="font-bold text-gray-900 mb-4">Your Profile</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Skill Level:</span>
            <span className="ml-2 font-medium text-gray-900 capitalize">{userProfile.skillLevel || 'Not set'}</span>
          </div>
          <div>
            <span className="text-gray-500">Budget:</span>
            <span className={`ml-2 font-medium ${
              budgetTier === 'budget' ? 'text-emerald-600' : 
              budgetTier === 'premium' ? 'text-purple-600' : 'text-blue-600'
            }`}>{budgetLabel}</span>
          </div>
          <div>
            <span className="text-gray-500">Handicap:</span>
            <span className="ml-2 font-medium text-gray-900">{userProfile.handicap || 'Not set'}</span>
          </div>
          <div>
            <span className="text-gray-500">Current Clubs:</span>
            <span className="ml-2 font-medium text-gray-900">{userProfile.golfBag?.length || 0}</span>
          </div>
        </div>
      </div>

      {/* Build Options */}
      <div className="space-y-4">
        <h3 className="text-lg font-bold text-gray-900">Choose Your Path</h3>

        {/* Option 1: Beginner Builder Wizard */}
        <Link 
          to="/beginner-builder"
          className="block bg-white rounded-xl border-2 border-purple-200 hover:border-purple-400 p-5 transition-all hover:shadow-md"
        >
          <div className="flex items-start gap-4">
            <div className="p-3 bg-purple-100 rounded-xl">
              <ShoppingBag className="w-6 h-6 text-purple-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-bold text-gray-900 mb-1">Complete Starter Set Wizard</h4>
              <p className="text-sm text-gray-600 mb-3">
                Answer a few questions and get AI-matched complete sets with specific club recommendations, 
                pricing, and shopping links.
              </p>
              <div className="flex items-center gap-2">
                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full font-medium">
                  Best for New Golfers
                </span>
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                  5 min
                </span>
              </div>
            </div>
            <div className="text-purple-500">→</div>
          </div>
        </Link>

        {/* Option 2: Equipment Fitting */}
        <Link 
          to="/club-optimizer?tab=equipment-fitting"
          className="block bg-white rounded-xl border-2 border-blue-200 hover:border-blue-400 p-5 transition-all hover:shadow-md"
        >
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-100 rounded-xl">
              <Target className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-bold text-gray-900 mb-1">Full Club Fitting</h4>
              <p className="text-sm text-gray-600 mb-3">
                Get detailed recommendations for each club category based on your swing data, 
                physical stats, and playing goals.
              </p>
              <div className="flex items-center gap-2">
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                  For Experienced Players
                </span>
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                  10 min
                </span>
              </div>
            </div>
            <div className="text-blue-500">→</div>
          </div>
        </Link>

        {/* Option 3: Manual Bag Setup */}
        <Link 
          to="/my-golf-bag"
          className="block bg-white rounded-xl border-2 border-gray-200 hover:border-gray-400 p-5 transition-all hover:shadow-md"
        >
          <div className="flex items-start gap-4">
            <div className="p-3 bg-gray-100 rounded-xl">
              <Plus className="w-6 h-6 text-gray-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-bold text-gray-900 mb-1">Add Clubs Manually</h4>
              <p className="text-sm text-gray-600 mb-3">
                Already have clubs? Add them to your bag one by one to enable the Bag Health Check analysis.
              </p>
              <div className="flex items-center gap-2">
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                  Flexible
                </span>
              </div>
            </div>
            <div className="text-gray-400">→</div>
          </div>
        </Link>
      </div>

      {/* Info Box */}
      <div className="bg-purple-50 rounded-xl p-4 flex items-start gap-3">
        <Lightbulb className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-purple-900 font-medium">Why Build a Matched Set?</p>
          <p className="text-sm text-purple-700">
            Clubs designed to work together create consistent gapping throughout your bag, 
            making club selection easier and lowering your scores.
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * Stat Card component for problem summary
 */
interface StatCardProps {
  count: number;
  label: string;
  color: 'red' | 'orange' | 'yellow' | 'green';
}

const StatCard: React.FC<StatCardProps> = ({ count, label, color }) => {
  const colorClasses = {
    red: 'bg-red-50 border-red-200 text-red-700',
    orange: 'bg-orange-50 border-orange-200 text-orange-700',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-700',
    green: 'bg-green-50 border-green-200 text-green-700'
  };

  const numberColors = {
    red: 'text-red-600',
    orange: 'text-orange-600',
    yellow: 'text-yellow-600',
    green: 'text-green-600'
  };

  return (
    <div className={`rounded-xl border-2 ${colorClasses[color]} p-3 text-center`}>
      <div className={`text-2xl font-bold ${numberColors[color]}`}>{count}</div>
      <div className="text-xs font-medium opacity-80">{label}</div>
    </div>
  );
};

/**
 * No Profile State
 */
const NoProfileState: React.FC = () => (
  <div className="max-w-4xl mx-auto p-6 space-y-8">
    <div className="text-center">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Club Optimizer</h1>
      <p className="text-gray-600">Get AI-powered analysis of your golf bag</p>
    </div>

    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 text-center">
      <Activity className="w-16 h-16 text-blue-500 mx-auto mb-4" />
      <h2 className="text-2xl font-semibold text-gray-900 mb-2">Set Up Your Profile First</h2>
      <p className="text-gray-600 mb-6">
        Add your clubs and distances to get a personalized bag health analysis with specific improvement recommendations.
      </p>
      <Link 
        to="/my-golf-bag" 
        className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
      >
        <Target size={20} />
        Set Up My Bag
      </Link>
    </div>

    {/* General Tips */}
    <div className="grid md:grid-cols-2 gap-4">
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 bg-red-100 rounded-lg">
            <AlertTriangle className="text-red-600 w-5 h-5" />
          </div>
          <h3 className="font-bold text-gray-900">Common Gap Issue</h3>
        </div>
        <p className="text-sm text-gray-600">
          Most amateur bags have a 25-35 yard gap between their longest iron and fairway wood. 
          A hybrid can bridge this scoring zone.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 bg-yellow-100 rounded-lg">
            <Target className="text-yellow-600 w-5 h-5" />
          </div>
          <h3 className="font-bold text-gray-900">Wedge Setup</h3>
        </div>
        <p className="text-sm text-gray-600">
          Proper wedge gapping (4° between each) gives you precision in the scoring zone. 
          Most golfers need 3-4 wedges for complete coverage.
        </p>
      </div>
    </div>
  </div>
);

export default Recommendations;
